import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { Flex, Select, Text, LoadingOrbit } from '@chatsdet/ui';
import { API_URL } from '../../config.js';

type KnownPageAnalysisPayload = {
  orgId?: string;
  propertyId: string;
  credentialId?: string | null;
};

type Organization = {
  id: string;
  name: string;
};

type Property = {
  id: string;
  name: string;
  baseUrl: string | null;
  orgId: string;
};

type Credential = {
  id: string;
  name: string;
  isDefault: boolean;
};

type KnownPageAnalysisJobConfigProps = {
  value: Partial<KnownPageAnalysisPayload>;
  onChange: (payload: KnownPageAnalysisPayload) => void;
  disabled?: boolean;
};

export function KnownPageAnalysisJobConfig({
  value,
  onChange,
  disabled = false,
}: KnownPageAnalysisJobConfigProps) {
  const [orgId, setOrgId] = React.useState<string | null>(value.orgId ?? null);
  const [propertyId, setPropertyId] = React.useState<string>(value.propertyId || '');
  const [credentialId, setCredentialId] = React.useState<string | null>(
    value.credentialId ?? null
  );

  // Fetch organizations
  const organizationsQuery = useQuery({
    queryKey: ['superadmin-organizations'],
    queryFn: async (): Promise<{ organizations: Organization[] }> => {
      const res = await fetch(`${API_URL}/superadmin/organizations`, {
        credentials: 'include',
      });
      if (!res.ok) throw new Error('Failed to fetch organizations');
      return res.json();
    },
  });

  // Fetch properties for selected org
  const propertiesQuery = useQuery({
    queryKey: ['superadmin-properties', orgId],
    queryFn: async (): Promise<{ properties: Property[] }> => {
      const res = await fetch(`${API_URL}/superadmin/organizations/${orgId}/properties`, {
        credentials: 'include',
      });
      if (!res.ok) throw new Error('Failed to fetch properties');
      return res.json();
    },
    enabled: Boolean(orgId),
  });

  // Fetch credentials for selected property
  const credentialsQuery = useQuery({
    queryKey: ['superadmin-credentials', propertyId, orgId],
    queryFn: async (): Promise<{ credentials: Credential[] }> => {
      if (!orgId || !propertyId) throw new Error('Missing orgId or propertyId');
      const res = await fetch(
        `${API_URL}/superadmin/properties/${propertyId}/credentials?orgId=${orgId}`,
        {
          credentials: 'include',
        }
      );
      if (!res.ok) throw new Error('Failed to fetch credentials');
      return res.json();
    },
    enabled: Boolean(propertyId) && Boolean(orgId),
  });

  // Set default org when organizations load (only if not already set)
  React.useEffect(() => {
    if (!orgId && organizationsQuery.data?.organizations.length) {
      const firstOrg = organizationsQuery.data.organizations[0];
      setOrgId(firstOrg!.id);
    }
  }, [organizationsQuery.data?.organizations, orgId]);

  // Set default property when properties load (only if not already set)
  React.useEffect(() => {
    if (!propertyId && propertiesQuery.data?.properties.length) {
      const firstProp = propertiesQuery.data.properties[0];
      setPropertyId(firstProp!.id);
    }
  }, [propertiesQuery.data?.properties, propertyId]);

  // Set default credential when credentials load (only if not already set, optional - can be null)
  React.useEffect(() => {
    if (credentialId === null && credentialsQuery.data?.credentials.length) {
      const defaultCred = credentialsQuery.data.credentials.find((c) => c.isDefault);
      const firstCred = credentialsQuery.data.credentials[0];
      setCredentialId(defaultCred?.id || firstCred!.id);
    }
  }, [credentialsQuery.data?.credentials, credentialId]);

  // Update payload when any field changes
  React.useEffect(() => {
    if (propertyId && propertiesQuery.data) {
      const property = propertiesQuery.data.properties.find((p) => p.id === propertyId);
      if (property) {
        const payload: KnownPageAnalysisPayload = {
          orgId: orgId ?? undefined,
          propertyId,
          credentialId: credentialId || undefined,
        };
        onChange(payload);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [propertyId, credentialId, orgId, propertiesQuery.data]);

  const orgOptions = React.useMemo(() => {
    return (organizationsQuery.data?.organizations || []).map((org) => ({
      id: org.id,
      label: org.name,
    }));
  }, [organizationsQuery.data]);

  const propertyOptions = React.useMemo(() => {
    return (propertiesQuery.data?.properties || []).map((prop) => ({
      id: prop.id,
      label: `${prop.name}${prop.baseUrl ? ` (${prop.baseUrl})` : ''}`,
    }));
  }, [propertiesQuery.data]);

  const credentialOptions = React.useMemo(() => {
    const options = (credentialsQuery.data?.credentials || []).map((cred) => ({
      id: cred.id,
      label: `${cred.name}${cred.isDefault ? ' (default)' : ''}`,
    }));

    // Add an option to not use a credential
    return [{ id: 'none', label: 'Auto-select credential (recommended)' }, ...options];
  }, [credentialsQuery.data]);

  if (organizationsQuery.isLoading) {
    return (
      <Flex align="center" justify="center" className="py-4">
        <LoadingOrbit size="md" />
      </Flex>
    );
  }

  return (
    <Flex direction="column" gap="md">
      <Select
        label="Organization"
        placeholder="Select organization"
        items={orgOptions}
        selectedKey={orgId ?? undefined}
        onSelectionChange={(key) => {
          setOrgId(String(key));
          setPropertyId(''); // Reset property when org changes
          setCredentialId(null); // Reset credential when org changes
        }}
        isDisabled={disabled || organizationsQuery.isLoading}
      />

      {orgId && (
        <Select
          label="Application"
          placeholder={propertiesQuery.isLoading ? 'Loading applications...' : 'Select application'}
          items={propertyOptions}
          selectedKey={propertyId || ''}
          onSelectionChange={(key) => {
            setPropertyId(String(key));
            setCredentialId(null); // Reset credential when property changes
          }}
          isDisabled={disabled || propertiesQuery.isLoading || !propertyOptions.length}
        />
      )}

      {propertyId && (
        <>
          <Select
            label="Credential (Optional)"
            placeholder={
              credentialsQuery.isLoading
                ? 'Loading credentials...'
                : credentialOptions.length > 1
                  ? 'Select credential'
                  : 'No credentials found'
            }
            items={credentialOptions}
            selectedKey={credentialId || 'none'}
            onSelectionChange={(key) => {
              const selectedKey = String(key);
              setCredentialId(selectedKey === 'none' ? null : selectedKey);
            }}
            isDisabled={disabled || credentialsQuery.isLoading}
          />
          <Text className="text-sm text-muted-foreground">
            If no credential is selected, the system will automatically choose the most appropriate
            credential for each page based on usage history.
          </Text>

          {propertiesQuery.data && (
            <div className="bg-muted/30 rounded-md p-3 mt-2">
              <Text size="xs" className="text-muted-foreground">
                <strong>Application:</strong>{' '}
                {propertiesQuery.data.properties.find((p) => p.id === propertyId)?.baseUrl}
              </Text>
            </div>
          )}
        </>
      )}
    </Flex>
  );
}
